// src/config/rolePermissionsV2.js

export const PERMISSIONS = Object.freeze({
  // Core / Navigation
  DASHBOARD: "dashboard",
  STAFF_OVERVIEW: "staff_overview",

  // Admin / Staff management
  MANAGE_ROLES: "manage_roles",
  MANAGE_ADMINS: "manage_admins",
  MANAGE_USER_TEAM: "manage_user_team",

  // Items
  VIEW_ITEMS: "view_items",
  EDIT_ITEMS: "edit_items",
  APPROVE_ITEMS: "approve_items",
  VERIFY_ITEMS: "verify_items",
  CHECK_ITEM_STATUS: "check_item_status",
  PUBLISH_ITEMS: "publish_items",

  // Users
  VIEW_USERS: "view_users",
  EDIT_USERS: "edit_users",
  MODERATE_USERS: "moderate_users",

  // RAP / Value
  EDIT_RAP: "edit_rap",
  EDIT_VALUE: "edit_value",
  VERIFY_RAP: "verify_rap",
  VERIFY_VALUE: "verify_value",

  // Analytics / Reporting
  ANALYZE_ITEM_HISTORY: "analyze_item_history",
  TRACK_ITEM_TRENDS: "track_item_trends",
  GENERATE_ITEM_REPORTS: "generate_item_reports",

  // Moderation / Content
  REVIEW_MODERATION: "review_moderation",
  MODERATE_CONTENT: "moderate_content",
  OVERSEE_MODERATORS: "oversee_moderators",

  // Announcements
  VIEW_ANNOUNCEMENTS: "view_announcements",
  EDIT_ANNOUNCEMENTS: "edit_announcements",

  // Ads
  VIEW_ADS: "view_ads",
  EDIT_ADS: "edit_ads",

  // Logs / Audit
  VIEW_LOGS: "view_logs",
});

export const rolePermissions = Object.freeze({
  // =====================================================
  // ADMIN / PLATFORM (Highest Trust)
  // =====================================================

  super_admin: [
    PERMISSIONS.DASHBOARD,
    PERMISSIONS.STAFF_OVERVIEW,

    PERMISSIONS.MANAGE_ROLES,
    PERMISSIONS.MANAGE_ADMINS,
    PERMISSIONS.MANAGE_USER_TEAM,

    PERMISSIONS.VIEW_ITEMS,
    PERMISSIONS.EDIT_ITEMS,
    PERMISSIONS.APPROVE_ITEMS,
    PERMISSIONS.VERIFY_ITEMS,
    PERMISSIONS.CHECK_ITEM_STATUS,
    PERMISSIONS.PUBLISH_ITEMS,

    PERMISSIONS.VIEW_USERS,
    PERMISSIONS.EDIT_USERS,
    PERMISSIONS.MODERATE_USERS,

    PERMISSIONS.EDIT_RAP,
    PERMISSIONS.EDIT_VALUE,
    PERMISSIONS.VERIFY_RAP,
    PERMISSIONS.VERIFY_VALUE,

    PERMISSIONS.ANALYZE_ITEM_HISTORY,
    PERMISSIONS.TRACK_ITEM_TRENDS,
    PERMISSIONS.GENERATE_ITEM_REPORTS,

    PERMISSIONS.REVIEW_MODERATION,
    PERMISSIONS.MODERATE_CONTENT,
    PERMISSIONS.OVERSEE_MODERATORS,

    PERMISSIONS.VIEW_ANNOUNCEMENTS,
    PERMISSIONS.EDIT_ANNOUNCEMENTS,
    PERMISSIONS.VIEW_ADS,
    PERMISSIONS.EDIT_ADS,

    PERMISSIONS.VIEW_LOGS,
  ],

  admin: [
    PERMISSIONS.DASHBOARD,
    PERMISSIONS.STAFF_OVERVIEW,

    PERMISSIONS.MANAGE_ADMINS,
    PERMISSIONS.MANAGE_USER_TEAM,

    PERMISSIONS.VIEW_ITEMS,
    PERMISSIONS.EDIT_ITEMS,
    PERMISSIONS.APPROVE_ITEMS,
    PERMISSIONS.VERIFY_ITEMS,
    PERMISSIONS.CHECK_ITEM_STATUS,
    PERMISSIONS.PUBLISH_ITEMS,

    PERMISSIONS.VIEW_USERS,
    PERMISSIONS.EDIT_USERS,
    PERMISSIONS.MODERATE_USERS,

    PERMISSIONS.REVIEW_MODERATION,

    PERMISSIONS.VIEW_ANNOUNCEMENTS,
    PERMISSIONS.EDIT_ANNOUNCEMENTS,
    PERMISSIONS.VIEW_ADS,
    PERMISSIONS.EDIT_ADS,

    PERMISSIONS.VIEW_LOGS,
  ],

  manager: [
    PERMISSIONS.DASHBOARD,
    PERMISSIONS.STAFF_OVERVIEW,

    PERMISSIONS.VIEW_ITEMS,
    PERMISSIONS.APPROVE_ITEMS,

    PERMISSIONS.VIEW_USERS,
    PERMISSIONS.MODERATE_USERS,

    PERMISSIONS.REVIEW_MODERATION,
    PERMISSIONS.OVERSEE_MODERATORS,

    PERMISSIONS.VIEW_LOGS,
  ],

  // =====================================================
  // MODERATION (Community Safety)
  // =====================================================

  senior_moderator: [
    PERMISSIONS.DASHBOARD,
    PERMISSIONS.STAFF_OVERVIEW,

    PERMISSIONS.VIEW_USERS,
    PERMISSIONS.MODERATE_USERS,

    PERMISSIONS.REVIEW_MODERATION,
    PERMISSIONS.MODERATE_CONTENT,
    PERMISSIONS.OVERSEE_MODERATORS,
  ],

  moderator: [
    PERMISSIONS.DASHBOARD,
    PERMISSIONS.STAFF_OVERVIEW,

    PERMISSIONS.VIEW_USERS,
    PERMISSIONS.MODERATE_USERS,

    PERMISSIONS.REVIEW_MODERATION,
    PERMISSIONS.MODERATE_CONTENT,
  ],

  content_moderator: [
    PERMISSIONS.DASHBOARD,
    PERMISSIONS.MODERATE_CONTENT,
  ],

  reviewer: [
    PERMISSIONS.DASHBOARD,
    PERMISSIONS.REVIEW_MODERATION,
  ],

  // =====================================================
  // ITEMS TEAM (Data Management)
  // =====================================================

  lead_item_editor: [
    PERMISSIONS.DASHBOARD,

    PERMISSIONS.VIEW_ITEMS,
    PERMISSIONS.EDIT_ITEMS,
    PERMISSIONS.APPROVE_ITEMS,
    PERMISSIONS.VERIFY_ITEMS,
    PERMISSIONS.CHECK_ITEM_STATUS,
  ],

  item_editor: [
    PERMISSIONS.DASHBOARD,
    PERMISSIONS.VIEW_ITEMS,
    PERMISSIONS.EDIT_ITEMS,
  ],

  item_verifier: [
    PERMISSIONS.DASHBOARD,
    PERMISSIONS.VIEW_ITEMS,
    PERMISSIONS.APPROVE_ITEMS,
    PERMISSIONS.VERIFY_ITEMS,
  ],

  item_status_checker: [
    PERMISSIONS.DASHBOARD,
    PERMISSIONS.VIEW_ITEMS,
    PERMISSIONS.APPROVE_ITEMS,
    PERMISSIONS.CHECK_ITEM_STATUS,
  ],

  item_analyst: [
    PERMISSIONS.DASHBOARD,
    PERMISSIONS.VIEW_ITEMS,
    PERMISSIONS.ANALYZE_ITEM_HISTORY,
    PERMISSIONS.TRACK_ITEM_TRENDS,
    PERMISSIONS.GENERATE_ITEM_REPORTS,
  ],

  // =====================================================
  // RAP / VALUE TEAM (Economy / Pricing)
  // =====================================================

  value_team: [
    PERMISSIONS.DASHBOARD,

    PERMISSIONS.VIEW_ITEMS,
  
    PERMISSIONS.EDIT_RAP,
    PERMISSIONS.EDIT_VALUE,
    PERMISSIONS.VERIFY_RAP,
    PERMISSIONS.VERIFY_VALUE,
  ],

  rap_value_editor: [
    PERMISSIONS.DASHBOARD,
    PERMISSIONS.VIEW_ITEMS,
    PERMISSIONS.EDIT_RAP,
    PERMISSIONS.EDIT_VALUE,
  ],

  rap_value_verifier: [
    PERMISSIONS.DASHBOARD,
    PERMISSIONS.VIEW_ITEMS,
    PERMISSIONS.VERIFY_RAP,
    PERMISSIONS.VERIFY_VALUE,
  ],

  // =====================================================
  // SUPPORT / USER TEAM (Future Ready)
  // =====================================================

  lead_user_editor: [
    PERMISSIONS.DASHBOARD,
    PERMISSIONS.VIEW_USERS,
    PERMISSIONS.EDIT_USERS,
    PERMISSIONS.MANAGE_USER_TEAM,
  ],

  user_editor: [
    PERMISSIONS.DASHBOARD,
    PERMISSIONS.VIEW_USERS,
    PERMISSIONS.EDIT_USERS,
  ],

  user_team: [
    PERMISSIONS.DASHBOARD,
    PERMISSIONS.VIEW_USERS,
    PERMISSIONS.EDIT_USERS,
  ],

  // =====================================================
  // OPTIONAL SPECIALIZED
  // =====================================================

  publish_team: [
    PERMISSIONS.DASHBOARD,
    PERMISSIONS.PUBLISH_ITEMS,
  ],

  // =====================================================
  // DEFAULT SAFE ROLE
  // =====================================================

  new_admin: [
    PERMISSIONS.DASHBOARD,
  ],
});